<script lang="ts" setup>
import { refDebounced } from '@vueuse/core'
import { LatLng } from 'leaflet'

type Coords = { lat: string, lng: string } | { lat: number, lng: number }

export interface MapProps {
  center?: Coords
  marker?: Coords
  addHanselmannMarkers?: boolean
  isMarkerDraggable?: boolean
  zoom?: number
  minZoom?: number
  maxZoom?: number
  boundRadiusInMeters?: number
  static?: boolean
  hideControls?: boolean
  style?: {
    height: string
    width: string
  }
}

const props = withDefaults(defineProps<MapProps>(), {
  center: () => ({ lat: 49.1283547, lng: 10.0423045 }),
  style: () => ({ height: '60vh', width: '100%' }),
  zoom: 10,
})
const emit = defineEmits<{
  (e: 'update:marker', data: { lat: string, lng: string }): void
}>()

const options = computed(() => {
  if (!props.static) {
    return {
      zoomControl: false,
    }
  }

  // Disable all forms of interaction on the map
  return {
    zoomControl: false,
    dragging: false,
    doubleClickZoom: false,
    boxZoom: false,
    scrollWheelZoom: false,
  }
})

const centerCordinates = computed<[number, number]>(() => {
  const lat = parseNumberOrStringToNumber(props.center.lat)
  const lng = parseNumberOrStringToNumber(props.center.lng)
  return [lat, lng]
})

// Marker Address Selection
const marker = ref<LatLng | undefined>(props.marker ? new LatLng(parseNumberOrStringToNumber(props.marker.lat), parseNumberOrStringToNumber(props.marker.lng)) : undefined)

const isMarkerInBounds = computed(() => {
  if (!props.boundRadiusInMeters || !marker.value) {
    return true
  }
  return isPointInRadius({ lat: `${marker.value.lat}`, lng: `${marker.value.lng}` }, props.center, props.boundRadiusInMeters)
})

const isMarkerSaveMessageShown = ref(false)
function showMarkerSaveMessage() {
  if (isMarkerSaveMessageShown.value) {
    return
  }
  isMarkerSaveMessageShown.value = true
  setTimeout(() => isMarkerSaveMessageShown.value = false, 1000)
}

const debouncedMarker = refDebounced(marker, 100)
watch(debouncedMarker, (value) => {
  if (value && isMarkerInBounds.value) {
    emit('update:marker', { lat: `${value.lat}`, lng: `${value.lng}` })
    showMarkerSaveMessage()
  }
})

// Location presets
const HANSELMANN_LOCATIONS: LatLng[] = [
  // Head office
  new LatLng(49.1283547, 10.0423045),
  // Logistics centre
  new LatLng(49.255689, 10.014610),
  // External warehouse
  new LatLng(49.128607, 10.046005),
]
</script>

<template>
  <LMap
    :center="centerCordinates"
    :zoom
    :min-zoom
    :max-zoom
    :options
    :use-global-leaflet="false"
    :style="{ position: 'relative', ...style }"
  >
    <!-- Tile Map -->
    <LTileLayer
      url="https://tile.openstreetmap.de/{z}/{x}/{y}.png"
      attribution="&amp;copy; <a href=&quot;https://www.openstreetmap.org/&quot;>OpenStreetMap</a> contributors"
      layer-type="base"
      :name="$t('map.layer.standard')"
    />

    <LTileLayer
      url="https://tiles.stadiamaps.com/tiles/alidade_satellite/{z}/{x}/{y}{r}.jpg"
      attribution="&copy; CNES, Distribution Airbus DS, © Airbus DS, © PlanetObserver (Contains Copernicus Data) | &copy; <a href=&quot;https://www.stadiamaps.com/&quot; target=&quot;_blank&quot;>Stadia Maps</a> &copy; <a href=&quot;https://openmaptiles.org/&quot; target=&quot;_blank&quot;>OpenMapTiles</a> &copy; <a href=&quot;https://www.openstreetmap.org/copyright&quot;>OpenStreetMap</a> contributors"
      layer-type="base"
      :name="$t('map.layer.satellite')"
    />

    <LControlLayers position="topright" />

    <!-- Hanselmann location markers -->
    <template v-if="addHanselmannMarkers">
      <LMarker v-for="(latLng, index) in HANSELMANN_LOCATIONS" :key="index" :lat-lng="latLng">
        <LIcon class-name="">
          <div class="relative">
            <Icon name="material-symbols:warehouse-rounded" size="30px" class="text-hos-blue-light" />
          </div>
        </LIcon>
      </LMarker>
    </template>

    <!-- Marker bounds indicator -->
    <LCircle
      v-if="boundRadiusInMeters"
      :lat-lng="centerCordinates"
      :radius="boundRadiusInMeters"
      :color="isMarkerInBounds ? '#9DB7DD' : 'red'"
    />

    <!-- Marker out of bounds warning message -->
    <div v-if="!isMarkerInBounds" class="absolute bottom-5 left-0 z-20 w-full pointer-events-none">
      <div class="bg-red-500 rounded px-2 pb-1.5 pt-1 max-w-sm mx-auto">
        <p class="text-sm text-center text-white">
          {{ $t('map.position.prompt.inBounds') }}
        </p>
      </div>
    </div>

    <!-- Marker Save message -->
    <div
      class="absolute top-2 left-0 z-20 w-full transition-opacity pointer-events-none"
      :class="{ 'opacity-0': !isMarkerSaveMessageShown, 'opacity-100': isMarkerSaveMessageShown }"
    >
      <div class="flex items-center gap-1 bg-green-500 rounded px-4 pb-1.5 pt-1 mx-auto w-fit">
        <Icon name="material-symbols:check-circle-outline-rounded" size="17" class="text-white mt-px" />
        <p class="text-sm text-white">
          {{ $t('map.position.prompt.saved') }}
        </p>
      </div>
    </div>

    <!-- Marker -->
    <LMarker v-if="marker" v-model:lat-lng="marker" :draggable="isMarkerDraggable" />

    <!-- Zoom and scale controls -->
    <template v-if="!static && !hideControls">
      <LControlScale position="topright" :imperial="false" :metric="true" />
      <LControlZoom position="topright" />
    </template>
  </LMap>
</template>

<style>
.leaflet-control-attribution.leaflet-control {
  font-size: 8.1px
}

.leaflet-pane {
  z-index: 0 !important
}
</style>
